import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Coins, Lock, Check, ArrowRight, BookOpen, Mic, FileSignature, AudioLines } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { celebrate } from "@/lib/celebrate";

type Item = {
  item_key: string; title: string; blurb: string; kind: string; cost: number;
  badge_key: string | null; duration_days: number | null; cooldown_days: number; to_path: string | null;
};
type Unlock = { item_key: string; unlocked_at: string; expires_at: string | null; consumed_at: string | null };

const ERRORS: Record<string, string> = {
  not_enough_points: "You need a few more points for this one.",
  badge_required: "Earn the badge first, then come back.",
  cooldown: "You've used this one recently. It opens again soon.",
};

const FREE_TOOLS = [
  { to: "/interview", label: "Mock Interview", icon: Mic },
  { to: "/resume", label: "Resume", icon: FileSignature },
  { to: "/journal", label: "Voice Journal", icon: AudioLines },
];

const COURSES = ["Thinking about careers in AI", "Your first portfolio", "Talking to mentors"];

export default function RewardStore({
  badgeNames, earnedBadge,
}: { badgeNames: Record<string, string>; earnedBadge: (key: string) => boolean }) {
  const { user } = useAuth();
  const reduce = useReducedMotion();
  const [items, setItems] = useState<Item[]>([]);
  const [unlocks, setUnlocks] = useState<Unlock[]>([]);
  const [balance, setBalance] = useState<number | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) return;
    const [cat, mine, bal] = await Promise.all([
      supabase.from("reward_catalog").select("*").order("sort"),
      supabase.from("reward_unlocks").select("item_key, unlocked_at, expires_at, consumed_at").eq("user_id", user.id),
      supabase.rpc("reward_points_balance"),
    ]);
    setItems((cat.data ?? []) as Item[]);
    setUnlocks((mine.data ?? []) as Unlock[]);
    setBalance(typeof bal.data === "number" ? bal.data : 0);
  }, [user]);

  useEffect(() => { void load(); }, [load]);

  const redeem = async (it: Item) => {
    setBusy(it.item_key);
    const { error } = await supabase.rpc("redeem_reward", { _item_key: it.item_key });
    setBusy(null);
    if (error) {
      const key = Object.keys(ERRORS).find((k) => error.message.includes(k));
      toast.error(key ? ERRORS[key] : "That didn't go through. Please try again.");
      return;
    }
    celebrate("big");
    toast.success(`Unlocked: ${it.title}`);
    void load();
  };

  const statusFor = (it: Item) => {
    const mine = unlocks.filter((u) => u.item_key === it.item_key);
    const now = Date.now();
    const active = mine.find((u) =>
      u.expires_at ? new Date(u.expires_at).getTime() > now : !u.consumed_at);
    if (active) return { state: "active" as const, until: active.expires_at };
    const recent = mine.find((u) => new Date(u.unlocked_at).getTime() > now - it.cooldown_days * 86400000);
    if (recent) {
      const opens = new Date(new Date(recent.unlocked_at).getTime() + it.cooldown_days * 86400000);
      return { state: "cooldown" as const, until: opens.toISOString() };
    }
    if (it.badge_key && !earnedBadge(it.badge_key)) return { state: "badge" as const };
    if ((balance ?? 0) < it.cost) return { state: "short" as const };
    return { state: "ready" as const };
  };

  const fmt = (iso: string | null | undefined) =>
    iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "";

  return (
    <section aria-labelledby="store-h" className="space-y-4">
      <div className="flex items-baseline justify-between">
        <h2 id="store-h" className="font-display text-xl font-bold text-foreground">Unlock with points</h2>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-accent/15 px-3 py-1 text-sm font-semibold text-accent">
          <Coins className="h-4 w-4" aria-hidden="true" />
          {balance ?? "…"} points
        </span>
      </div>
      <p className="text-sm text-muted-foreground">Every XP you earn is a point to spend. Some rewards also need a badge.</p>

      <ul className="space-y-3">
        {items.map((it, i) => {
          const st = statusFor(it);
          return (
            <motion.li key={it.item_key}
              initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.06 }}
              className="glass-card lift flex flex-col gap-3 rounded-3xl p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="font-semibold text-foreground">{it.title}</p>
                <p className="mt-1 text-sm text-muted-foreground">{it.blurb}</p>
                <p className="mt-2 text-xs text-muted-foreground">
                  {it.cost} points
                  {it.badge_key ? ` · needs the “${badgeNames[it.badge_key] ?? it.badge_key}” badge` : ""}
                  {it.duration_days ? ` · lasts ${it.duration_days} days` : ""}
                </p>
              </div>
              <div className="shrink-0">
                {st.state === "active" ? (
                  <Button asChild variant="secondary" className="h-12 rounded-2xl">
                    <Link to={it.to_path ?? "/rewards"}>
                      <Check className="mr-2 h-4 w-4" aria-hidden="true" />
                      {st.until ? `Open until ${fmt(st.until)}` : "Ready to use"}
                    </Link>
                  </Button>
                ) : st.state === "ready" ? (
                  <Button className="h-12 rounded-2xl" disabled={busy === it.item_key} onClick={() => redeem(it)}>
                    {busy === it.item_key ? "Unlocking…" : `Unlock for ${it.cost}`}
                  </Button>
                ) : (
                  <p className="inline-flex min-h-12 items-center gap-2 text-sm text-muted-foreground">
                    <Lock className="h-4 w-4" aria-hidden="true" />
                    {st.state === "badge" && "Earn the badge first"}
                    {st.state === "short" && `${it.cost - (balance ?? 0)} more points`}
                    {st.state === "cooldown" && `Opens again ${fmt(st.until)}`}
                  </p>
                )}
              </div>
            </motion.li>
          );
        })}
      </ul>

      <div className="glass-card rounded-3xl p-5">
        <p className="text-sm font-semibold text-foreground">Included with your membership — no points needed</p>
        <div className="mt-3 flex flex-wrap gap-2">
          {FREE_TOOLS.map(({ to, label, icon: Icon }) => (
            <Link key={to} to={to}
              className="lift inline-flex min-h-11 items-center gap-2 rounded-2xl bg-muted/40 px-4 text-sm text-foreground">
              <Icon className="h-4 w-4 text-primary" aria-hidden="true" /> {label}
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            </Link>
          ))}
        </div>
      </div>

      <div>
        <p className="mb-2 text-sm font-semibold text-foreground">Courses — coming soon</p>
        <ul className="grid gap-3 sm:grid-cols-3">
          {COURSES.map((c) => (
            <li key={c} className="glass-card rounded-3xl p-4 opacity-70">
              <BookOpen className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              <p className="mt-2 text-sm font-medium text-foreground">{c}</p>
              <p className="mt-1 text-xs text-muted-foreground">Opening soon</p>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
